import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { LogoFull } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      toast.success("Welcome back to the studio.");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-[0.06]">
        <img src="https://images.unsplash.com/photo-1651862589463-076de32b52c4?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <div className="flex justify-center mb-9"><LogoFull /></div>
        <form onSubmit={submit} className="border border-white/8 bg-[#14171D]/90 backdrop-blur-xl rounded-2xl p-8 sm:p-10" data-testid="admin-login-form">
          <p className="font-mono-spec text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Studio access</p>
          <h1 className="font-serif-display text-2xl text-[#F5F3EF] mb-8">Admin panel sign in</h1>
          {error && (
            <div className="mb-5 text-[13px] text-red-300 bg-red-500/10 border border-red-500/25 rounded-lg px-4 py-3" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <label className="block font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#666F7D] mb-2">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="w-full bg-[#0E1116] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F5F3EF] mb-5"
            placeholder="admin@printedtubesdirect.com"
            data-testid="admin-email-input"
          />
          <label className="block font-mono-spec text-[10px] uppercase tracking-[0.22em] text-[#666F7D] mb-2">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            className="w-full bg-[#0E1116] border border-white/10 rounded-xl px-4 py-3.5 text-sm text-[#F5F3EF] mb-7"
            placeholder="••••••••••"
            data-testid="admin-password-input"
          />
          <button
            type="submit" disabled={loading}
            data-testid="admin-login-submit-btn"
            className="gold-btn w-full text-[#0B0D10] font-semibold text-sm py-4 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 size={15} className="animate-spin" />}
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <Link to="/" className="mt-6 flex items-center justify-center gap-2 text-[13px] text-[#666F7D] hover:text-[#D9A76A] transition-colors" data-testid="admin-back-link">
          <ArrowLeft size={14} /> Back to the website
        </Link>
      </motion.div>
    </div>
  );
}
